import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useRef, useState } from 'react';

import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';

export interface FileDropZoneProps {
	/** Called with the files dropped or chosen. Never called with an empty list. */
	onFiles: (files: Array<File>) => void;
	/**
	 * PD-16713 — called with the files left out (not matching `accept`, or past the first
	 * when `multiple` is off), so the caller can say why. Without it they are dropped
	 * silently. Never called with an empty list.
	 */
	onRejected?: (files: Array<File>) => void;
	/** The file input's `accept`; also filters dropped files by type. */
	accept?: string;
	multiple?: boolean;
	disabled?: boolean;
	/** Main line, e.g. "Drop pictures here, or choose files". Caller translates. */
	label?: string;
	/**
	 * A shorter main line for phone widths, where there is nothing to drag from (e.g.
	 * "Choose files"). Caller translates.
	 */
	phoneLabel?: string;
	/** Second line, e.g. accepted types and size limit. Caller translates. */
	hint?: string;
	/** A single-row version for the top of a list, instead of a tall panel. */
	compact?: boolean;
	className?: string;
}

/** Whether a file matches an `accept` list (MIME types, `type/*` wildcards, `.ext`). */
export function fileMatchesAccept(
	file: Pick<File, 'name' | 'type'>,
	accept: string | undefined,
): boolean {
	if (accept == null || accept.trim() === '') {
		return true;
	}
	const name = file.name.toLowerCase();
	const type = file.type.toLowerCase();
	return accept
		.split(',')
		.map((part) => part.trim().toLowerCase())
		.filter((part) => part !== '')
		.some((part) => {
			if (part.startsWith('.')) {
				return name.endsWith(part);
			}
			if (part.endsWith('/*')) {
				return type.startsWith(part.slice(0, -1));
			}
			return type === part;
		});
}

/**
 * PD-16713 — the files a drop or a choice delivers, split into those the zone takes and
 * those it leaves out: any that do not match `accept`, and every file past the first when
 * only one is taken.
 */
export function splitDroppedFiles<T extends Pick<File, 'name' | 'type'>>(
	files: ReadonlyArray<T>,
	accept: string | undefined,
	multiple: boolean,
): { accepted: Array<T>; rejected: Array<T> } {
	const accepted: Array<T> = [];
	const rejected: Array<T> = [];
	for (const file of files) {
		if (
			fileMatchesAccept(file, accept) &&
			(multiple || accepted.length === 0)
		) {
			accepted.push(file);
		} else {
			rejected.push(file);
		}
	}
	return { accepted, rejected };
}

/**
 * PD-16339 — a place to drop files, which also opens the file chooser when clicked or
 * activated from the keyboard. Files that do not match `accept` are left out here so
 * the caller only ever sees what it asked for; the server still checks every file.
 *
 * There was no drop target in the app before this; uploads were a button over a hidden
 * file input. Use this where people add several files at once (a library, an
 * attachment list). A single-image field is still better as a button.
 */
export function FileDropZone({
	onFiles,
	onRejected,
	accept,
	multiple = true,
	disabled = false,
	label,
	phoneLabel,
	hint,
	compact = false,
	className,
}: FileDropZoneProps): React.ReactElement {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	// Counted rather than a flag: dragenter/dragleave fire for every child element.
	const [dragDepth, setDragDepth] = useState(0);
	const isOver = dragDepth > 0 && !disabled;

	const deliver = useCallback(
		(list: FileList | null): void => {
			if (list == null || disabled) {
				return;
			}
			const { accepted, rejected } = splitDroppedFiles(
				Array.from(list),
				accept,
				multiple,
			);
			if (accepted.length > 0) {
				onFiles(accepted);
			}
			if (rejected.length > 0) {
				onRejected?.(rejected);
			}
		},
		[accept, disabled, multiple, onFiles, onRejected],
	);

	const open = (): void => {
		if (!disabled) {
			inputRef.current?.click();
		}
	};

	return (
		<div
			role="button"
			tabIndex={disabled ? -1 : 0}
			aria-disabled={disabled}
			aria-label={label ?? t('Drop files here, or choose files')}
			onClick={open}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					open();
				}
			}}
			onDragEnter={(event) => {
				event.preventDefault();
				setDragDepth((depth) => depth + 1);
			}}
			onDragLeave={(event) => {
				event.preventDefault();
				setDragDepth((depth) => Math.max(0, depth - 1));
			}}
			onDragOver={(event) => {
				// Required for the drop event to fire at all.
				event.preventDefault();
				event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
			}}
			onDrop={(event) => {
				event.preventDefault();
				setDragDepth(0);
				deliver(event.dataTransfer.files);
			}}
			className={cn(
				'rounded-lg border-2 border-dashed transition-colors focus:outline-none focus:ring-2 focus:ring-accent',
				compact
					? 'flex items-center gap-3 px-4 py-3'
					: 'flex flex-col items-center justify-center gap-2 px-6 py-8 text-center',
				isOver ? 'border-accent bg-subtle' : 'border-default bg-surface',
				disabled
					? 'cursor-not-allowed opacity-60'
					: 'cursor-pointer hover:border-accent',
				className,
			)}
		>
			<Icon
				icon={faCloudArrowUp}
				size={compact ? 'lg' : '2x'}
				className={isOver ? 'text-accent' : 'text-muted'}
			/>
			<div className={compact ? 'min-w-0' : undefined}>
				{phoneLabel != null ? (
					<p className="text-sm font-medium text-default">
						<span className="sm:hidden">{phoneLabel}</span>
						<span className="hidden sm:inline">
							{label ?? t('Drop files here, or choose files')}
						</span>
					</p>
				) : (
					<p className="text-sm font-medium text-default">
						{label ?? t('Drop files here, or choose files')}
					</p>
				)}
				{hint != null && <p className="text-xs mt-1 text-muted">{hint}</p>}
			</div>
			<input
				ref={inputRef}
				type="file"
				className="hidden"
				accept={accept}
				multiple={multiple}
				disabled={disabled}
				onClick={(event) => {
					event.stopPropagation();
				}}
				onChange={(event) => {
					deliver(event.target.files);
					// Let the same file be chosen again after a failed upload.
					event.target.value = '';
				}}
			/>
		</div>
	);
}
